import { Button, Icon, Tooltip, useTooltipText } from "@saviynt/design-system";
import { useCallback } from "react";
import { useIntl } from "react-intl";

export default function CopyButton({
  text,
  ariaLabel,
}: {
  text: unknown;
  ariaLabel: string;
}) {
  const intl = useIntl();
  const {
    tooltipText,
    handleTooltipTriggerClick,
    handleTooltipClose,
    tooltipCloseDelayAfterClick,
  } = useTooltipText({
    initialText: intl.formatMessage({
      defaultMessage: "Copy",
      id: "Eo0Htz",
      description: "Tooltip text for copy button in metadata tab",
    }),
    updatedText: intl.formatMessage({
      defaultMessage: "Copied",
      id: "dTovFT",
      description: "Tooltip text after clicking copy button in metadata tab",
    }),
    closeDelayAfterClick: 3000,
  });

  const handleCopyToClipboard = useCallback(async (): Promise<void> => {
    if (!text) {
      console.warn("No data to copy to clipboard");

      return;
    }

    try {
      const textToCopy =
        typeof text === "object" ? JSON.stringify(text, null, 2) : String(text);
      await navigator.clipboard.writeText(textToCopy);
    } catch (error) {
      console.error("Failed to copy text to clipboard:", error);
    }
  }, [text]);

  const handleCopy = useCallback(async (): Promise<void> => {
    handleTooltipTriggerClick();
    await handleCopyToClipboard();
  }, [handleTooltipTriggerClick, handleCopyToClipboard]);

  return (
    <Tooltip
      text={tooltipText}
      placement="top"
      closeDelayAfterClick={tooltipCloseDelayAfterClick}
      onClose={handleTooltipClose}
      isDisabled={tooltipText === "Copied"}
      trigger={
        <Button
          type="button"
          kind="ghost"
          size="small"
          isSuccess={tooltipText === "Copied"}
          leftIcon={
            <Icon
              kind={tooltipText === "Copied" ? "Check" : "Copy"}
              size="medium"
              color="neutral-100"
            />
          }
          onClick={handleCopy}
          dataTestId="copy-button"
          ariaLabel={ariaLabel}
        />
      }
    />
  );
}
